/**
 * API Service for authenticated requests to the backend
 * Handles token injection, transparent OAuth token refresh on 401, and
 * common error responses.
 */
import * as tokenStore from "./tokenStore";
import { isTransientAuthError } from "./authErrors";

export const API_BASE_URL = (process.env.REACT_APP_API_URL || "http://localhost:4000").replace(/\/+$/, '');

// ── Activity callback ─────────────────────────────────────────────────────────
// auth-hook.js registers a callback here after login so that every successful
// API response resets the inactivity timer (mirroring the refresh token's
// server-side idle window).
let _activityCallback = null;
export const setActivityCallback = (cb) => { _activityCallback = cb; };

// ── Logout callback ────────────────────────────────────────────────────────────
// Called when a 401 cannot be recovered by a token refresh, so the user is
// immediately redirected to the login page.
let _logoutCallback = null;
export const setLogoutCallback = (cb) => { _logoutCallback = cb; };

/**
 * Best token to send: the store's live access token when available (it may
 * have rotated since the caller captured its copy), otherwise the passed one.
 */
const effectiveToken = (token) => {
  const current = tokenStore.getAccessToken();
  return current && !tokenStore.isExpired() ? current : token;
};

const sessionExpiredError = (endpoint, method) => {
  const error = new Error("Your session has expired. Please login again.");
  error.status = 401;
  error.endpoint = endpoint;
  error.method = method;
  return error;
};

const serverUnreachableError = (endpoint, method) => {
  const error = new Error(
    "Can't reach the server right now — you're still signed in. Please try again in a moment."
  );
  error.status = 503;
  error.code = "network";
  error.endpoint = endpoint;
  error.method = method;
  return error;
};

/**
 * One transparent recovery attempt for a 401: rotate the refresh token
 * (single-flight + cross-tab lock inside tokenStore) and hand back a fresh
 * access token. When the refresh fails because the server is unreachable
 * (network / 5xx / 429 — e.g. Maestro restarting) the session is kept and the
 * request fails with a retryable error; only a rejected session logs out.
 */
const recoverFrom401 = async (endpoint, method) => {
  try {
    return await tokenStore.refresh();
  } catch (err) {
    if (isTransientAuthError(err)) throw serverUnreachableError(endpoint, method);
    _logoutCallback?.();
    throw sessionExpiredError(endpoint, method);
  }
};

/**
 * Make an authenticated API request
 * @param {string} endpoint - API endpoint (e.g., "/v1/projects")
 * @param {object} options - Fetch options (method, body, headers, etc.)
 * @param {string} token - Access token from auth context
 * @returns {Promise<object>} - Response data
 */
export const authenticatedFetch = async (endpoint, options = {}, token) => {
  const method = options.method || 'GET';
  const sendToken = options._retried ? token : effectiveToken(token);
  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };
  if (sendToken) {
    headers.Authorization = `Bearer ${sendToken}`;
  }

  const config = { ...options, headers };

  try {
    let response;
    try {
      response = await fetch(`${API_BASE_URL}${endpoint}`, config);
    } catch (networkError) {
      // fetch itself failed (offline, refused, CORS on a gateway error page):
      // tag it so callers can tell "unreachable" from a real answer.
      networkError.code = networkError.code || "network";
      throw networkError;
    }

    if (response.status === 401) {
      if (!options._retried && sendToken) {
        // Access token lapsed mid-flight — refresh once and retry.
        const fresh = await recoverFrom401(endpoint, method);
        return authenticatedFetch(endpoint, { ...options, _retried: true }, fresh);
      }
      _logoutCallback?.();
      throw sessionExpiredError(endpoint, method);
    }

    if (response.status === 403) {
      const error = new Error("You don't have permission to perform this action.");
      error.status = 403;
      error.endpoint = endpoint;
      error.method = method;
      // Keep the generic message (existing callers depend on it), but expose
      // the backend's specific reason as `details` when it sends one — some
      // 403s carry an actionable message beyond "no permission" (e.g. Geode's
      // "python steps require the geode.python permission").
      const errorData = await response.json().catch(() => ({}));
      if (errorData.message) error.details = errorData.message;
      throw error;
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error = new Error(errorData.message || `Request failed with status ${response.status}`);
      error.status = response.status;
      error.endpoint = endpoint;
      error.method = method;
      error.details = errorData.details || errorData.errors || errorData.error || null;
      // The whole parsed body, for endpoints that answer a failure with
      // structured data the caller can act on rather than just a message —
      // e.g. the fit-duplicate 409 naming the fit to open instead.
      error.body = errorData;
      throw error;
    }

    // Parse and return JSON response
    const data = await response.json();
    // Notify auth hook that the user made a successful request — resets inactivity timer.
    _activityCallback?.();
    return data;
  } catch (error) {
    // Attach request context to network-level errors (e.g. CORS, no connection)
    if (!error.endpoint) {
      error.endpoint = endpoint;
      error.method = method;
    }
    console.error("API Request Error:", error);
    throw error;
  }
};

/**
 * Authenticated request whose response is a stream (text/event-stream).
 * Same token handling as authenticatedFetch — one transparent refresh on a
 * 401 — but on success it hands back the Response itself so the caller can
 * read `response.body` as it arrives. Failures throw the same error shapes
 * as authenticatedFetch (status, details, body).
 * @param {string} endpoint - API endpoint (e.g., "/v1/assistant/chat")
 * @param {object} options - Fetch options (method, body, headers, signal)
 * @param {string} token - Access token from auth context
 * @returns {Promise<Response>}
 */
export const authenticatedStream = async (endpoint, options = {}, token) => {
  const method = options.method || 'GET';
  const sendToken = options._retried ? token : effectiveToken(token);
  const headers = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
    ...options.headers,
  };
  if (sendToken) headers.Authorization = `Bearer ${sendToken}`;

  let response;
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });
  } catch (networkError) {
    networkError.code = networkError.code || "network";
    networkError.endpoint = endpoint;
    networkError.method = method;
    throw networkError;
  }

  if (response.status === 401) {
    if (!options._retried && sendToken) {
      const fresh = await recoverFrom401(endpoint, method);
      return authenticatedStream(endpoint, { ...options, _retried: true }, fresh);
    }
    _logoutCallback?.();
    throw sessionExpiredError(endpoint, method);
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error(
      response.status === 403
        ? "You don't have permission to perform this action."
        : errorData.message || `Request failed with status ${response.status}`
    );
    error.status = response.status;
    error.endpoint = endpoint;
    error.method = method;
    error.details = errorData.details || errorData.message || null;
    error.body = errorData;
    throw error;
  }

  _activityCallback?.();
  return response;
};

/**
 * Convenience methods for common HTTP verbs
 */
export const apiService = {
  get: (endpoint, token, customHeaders = {}) => 
    authenticatedFetch(endpoint, { method: "GET", headers: customHeaders }, token),

  post: (endpoint, data, token, customHeaders = {}) =>
    authenticatedFetch(
      endpoint,
      {
        method: "POST",
        body: JSON.stringify(data),
        headers: customHeaders,
      },
      token
    ),

  patch: (endpoint, data, token, customHeaders = {}) =>
    authenticatedFetch(
      endpoint,
      {
        method: "PATCH",
        body: JSON.stringify(data),
        headers: customHeaders,
      },
      token
    ),

  put: (endpoint, data, token, customHeaders = {}) =>
    authenticatedFetch(
      endpoint,
      {
        method: "PUT",
        body: JSON.stringify(data),
        headers: customHeaders,
      },
      token
    ),

  delete: (endpoint, token, customHeaders = {}) =>
    authenticatedFetch(
      endpoint,
      {
        method: "DELETE",
        headers: customHeaders,
      },
      token
    ),

  /**
   * Upload a file using multipart/form-data
   * @param {string} endpoint - API endpoint
   * @param {FormData} formData - FormData object containing the file
   * @param {string} token - JWT token
   * @returns {Promise<object>} - Response data
   */
  uploadFile: async (endpoint, formData, token, _retried = false) => {
    const headers = {};
    const sendToken = _retried ? token : effectiveToken(token);
    if (sendToken) {
      headers.Authorization = `Bearer ${sendToken}`;
    }

    // Don't set Content-Type header - let browser set it with boundary for multipart/form-data
    const config = {
      method: "POST",
      headers,
      body: formData,
    };

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

      if (response.status === 401) {
        if (!_retried && sendToken) {
          const fresh = await recoverFrom401(endpoint, "POST");
          return apiService.uploadFile(endpoint, formData, fresh, true);
        }
        _logoutCallback?.();
        throw sessionExpiredError(endpoint, "POST");
      }

      if (response.status === 403) {
        const error = new Error("You don't have permission to perform this action.");
        error.status = 403;
        throw error;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Upload failed with status ${response.status}`);
      }

      const data = await response.json();
      _activityCallback?.();
      return data;
    } catch (error) {
      console.error("File Upload Error:", error);
      throw error;
    }
  },
};

/**
 * Simulation API functions
 */
export const simulationService = {
  /**
   * Queue a simulation run.
   * @param {string} scenarioId - Scenario MongoDB ObjectId (required)
   * @param {string} projectId - Project MongoDB ObjectId (sent as x-project-id header)
   * @param {string|null} companyId - Company MongoDB ObjectId (sent as x-company-id header, optional)
   * @param {string|null} name - Optional simulation name
   * @param {string|null} modelVersion - Simulation engine version (e.g. "dolomite", "corestone", "geode")
   * @param {object} input - Simulation input (e.g. { modelParameters: { ... } }). When
   *   `modelVersion` is "geode", include `geodeDefinitionId` here — it is lifted out to the
   *   top level of the request body (Maestro's contract), not sent nested under `input`.
   * @param {string} token - JWT token
   * @returns {Promise<{simulationId: string, status: string}>}
   */
  runSimulation: (scenarioId, projectId, companyId, name, modelVersion, input, token) => {
    const { geodeDefinitionId, ...restInput } = input || {};
    return authenticatedFetch(
      "/v1/simulation/run",
      {
        method: "POST",
        body: JSON.stringify({
          scenarioId, name, modelVersion,
          ...(geodeDefinitionId ? { geodeDefinitionId } : {}),
          input: restInput,
        }),
        headers: {
          "x-project-id": projectId,
          ...(companyId && { "x-company-id": companyId }),
        },
      },
      token
    );
  },

  /**
   * Poll current status of a simulation run.
   * @param {string} simulationId - Simulation MongoDB ObjectId
   * @param {string} token - JWT token
   * @returns {Promise<{simulationId: string, status: string, progress: number, currentDay: number}>}
   */
  getSimulationStatus: (simulationId, token) =>
    authenticatedFetch(
      `/v1/simulation/status/${simulationId}`,
      { method: "GET" },
      token
    ),

  /**
   * Get the active (or most recent) simulation for a scenario.
   * Returns the running/queued sim if one exists, otherwise the last terminal sim.
   * Throws with status 404 when no simulations exist for the scenario.
   * @param {string} scenarioId - Scenario MongoDB ObjectId
   * @param {string} token - JWT token
   * @returns {Promise<{simulationId, status, progressPercent, currentDay, totalDays, createdAt, errorMessage?}>}
   */
  getActiveSimulation: (scenarioId, token) =>
    authenticatedFetch(
      `/v1/simulation/scenario/${encodeURIComponent(scenarioId)}/active`,
      { method: "GET" },
      token
    ),

  /**
   * Cancel a queued or running simulation.
   * @param {string} simulationId - Simulation MongoDB ObjectId
   * @param {string} token - JWT token
   * @returns {Promise<{simulationId: string, status: string}>}
   */
  cancelSimulation: (simulationId, token) =>
    authenticatedFetch(
      `/v1/simulation/cancel/${encodeURIComponent(simulationId)}`,
      { method: "POST" },
      token
    ),

  /**
   * Fetch simulation results after completion.
   * @param {string} simulationId - Simulation MongoDB ObjectId
   * @param {string} token - JWT token
   * @returns {Promise<{simulationId: string, result: object}>}
   */
  getSimulationResult: (simulationId, token) =>
    authenticatedFetch(
      `/v1/simulation/result/${simulationId}`,
      { method: "GET" },
      token
    ),

  /**
   * Get a 1-hour presigned S3 URL to download the Parquet result file.
   * @param {string} simulationId - Simulation MongoDB ObjectId
   * @param {string} token - JWT token
   * @returns {Promise<{url: string}>}
   */
  getParquetDownloadUrl: (simulationId, token) =>
    authenticatedFetch(
      `/v1/simulation/result/${encodeURIComponent(simulationId)}/parquet`,
      { method: "GET" },
      token
    ),

  /**
   * Get a 1-hour presigned S3 URL to download a specific heap-state snapshot Parquet file.
   * @param {string} simulationId - Simulation MongoDB ObjectId
   * @param {string} date - Snapshot date in YYYY-MM-DD format
   * @param {string} token - JWT token
   * @returns {Promise<{date: string, url: string, expiresInSeconds: number}>}
   */
  getSnapshotDownloadUrl: (simulationId, date, token) =>
    authenticatedFetch(
      `/v1/simulation/result/${encodeURIComponent(simulationId)}/snapshot/${encodeURIComponent(date)}`,
      { method: "GET" },
      token
    ),

  /**
   * Get size metadata for all snapshot Parquet files without generating download URLs.
   * Use this to show the user a total download size before committing to Cache All.
   * Sizes are lazily cached server-side after the first call.
   * @param {string} simulationId - Simulation MongoDB ObjectId
   * @param {string} token - JWT token
   * @returns {Promise<{snapshots: Array<{date: string, size_bytes: number|null}>, totalSizeBytes: number}>}
   */
  getSnapshotsInfo: (simulationId, token) =>
    authenticatedFetch(
      `/v1/simulation/result/${encodeURIComponent(simulationId)}/snapshots/info`,
      { method: 'GET' },
      token
    ),

  /**
   * Get presigned S3 URLs for ALL snapshot Parquet files in a single request.
   * Use instead of individual getSnapshotDownloadUrl calls when caching all snapshots.
   * @param {string} simulationId - Simulation MongoDB ObjectId
   * @param {string} token - JWT token
   * @returns {Promise<{snapshots: Array<{date: string, url: string|null, error?: string}>, expiresInSeconds: number}>}
   */
  getAllSnapshotUrls: (simulationId, token) =>
    authenticatedFetch(
      `/v1/simulation/result/${encodeURIComponent(simulationId)}/snapshots`,
      { method: 'GET' },
      token
    ),

  /**
   * Check the health of the simulation engine (Étude). Admin only.
   * @param {string} token - JWT token
   * @returns {Promise<{status: string, etude: {status: string, latencyMs: number}, timestamp: string}>}
   */
  checkEngineHealth: (token) =>
    authenticatedFetch("/v1/system/health", { method: "GET" }, token),

  /**
   * List simulations, optionally filtered by project and/or scenario.
   * @param {string|null} projectId - Optional project filter
   * @param {string|null} scenarioId - Optional scenario filter
   * @param {string} token - JWT token
   * @returns {Promise<{simulations: Array}>}
   */
  listSimulations: (projectId, scenarioId, token) => {
    const params = new URLSearchParams();
    if (projectId) params.set("projectId", projectId);
    if (scenarioId) params.set("scenarioId", scenarioId);
    const query = params.toString();
    return authenticatedFetch(
      `/v1/simulation${query ? `?${query}` : ""}`,
      {
        method: "GET",
        // The list route has no simulation :id to infer project context from,
        // so the permission check needs the x-project-id header — without it,
        // non-admins are denied regardless of their project permissions.
        ...(projectId && { headers: { "x-project-id": projectId } }),
      },
      token
    );
  },

  /**
   * Delete stored result data for a completed simulation.
   * The simulation document itself is retained; only the result + Parquet file are removed.
   * @param {string} simulationId - Simulation MongoDB ObjectId
   * @param {string} token - JWT token
   * @returns {Promise<{simulationId: string, message: string}>}
   */
  deleteSimulationResult: (simulationId, token) =>
    authenticatedFetch(
      `/v1/simulation/result/${encodeURIComponent(simulationId)}`,
      { method: "DELETE" },
      token
    ),

  /**
   * Fetch all log entries recorded during a simulation run.
   * @param {string} simulationId - Simulation MongoDB ObjectId
   * @param {string} token - JWT token
   * @returns {Promise<{logs: Array<{severity: string, code: string, message: string, day?: number, timestamp?: string}>}>}
   */
  getSimulationLogs: (simulationId, token) =>
    authenticatedFetch(
      `/v1/simulation/${encodeURIComponent(simulationId)}`,
      { method: "GET" },
      token
    ).then(data => ({ logs: data.simulation?.logs ?? [] })),

  /**
   * Fetch a single simulation document by ID.
   * Returns the full simulation record including parquetAttributes.
   * @param {string} simulationId - Simulation MongoDB ObjectId
   * @param {string} token - JWT token
   * @returns {Promise<object|null>} The simulation document, or null if not found
   */
  getSimulationById: (simulationId, token) =>
    authenticatedFetch(
      `/v1/simulation/${encodeURIComponent(simulationId)}`,
      { method: "GET" },
      token
    ).then(data => data.simulation ?? null),

  /**
   * Rename a simulation run. Updates the run's stored name (input.name).
   * @param {string} simulationId - Simulation MongoDB ObjectId
   * @param {string} name - New non-empty run name
   * @param {string} token - JWT token
   * @returns {Promise<{simulationId: string, name: string, simulation: object}>}
   */
  renameSimulation: (simulationId, name, token) =>
    authenticatedFetch(
      `/v1/simulation/${encodeURIComponent(simulationId)}`,
      {
        method: "PATCH",
        body: JSON.stringify({ name }),
      },
      token
    ),
};

export const heapService = {
  /**
   * Fetch in-database statistics for all heaps in a scenario.
   * GET /v1/scenario/:id/heap/statistics
   * Returns aggregated fields including stacking_date_range and leach_date_range.
   */
  getHeapStatistics: (scenarioId, token) =>
    authenticatedFetch(
      `/v1/scenario/${encodeURIComponent(scenarioId)}/heap/statistics`,
      { method: "GET" },
      token
    ),

  /**
   * Server-side summary of one heap's stored leach schedule.
   * GET /v1/scenario/:id/heap/:heapId/leach-schedule
   * Returns { hasSchedule, firstLeachDay, lastLeachDay, ... } (YYYY-MM-DD).
   */
  getLeachScheduleSummary: (scenarioId, heapId, token) =>
    authenticatedFetch(
      `/v1/scenario/${encodeURIComponent(scenarioId)}/heap/${encodeURIComponent(heapId)}/leach-schedule`,
      { method: "GET" },
      token
    ),
};

export default apiService;
